import React, { useState, useEffect } from 'react';
import { Modal } from '@common/Modal';
import { Button } from '@common/Button';
import { Search, User, Mail, Plus, X } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../../../config/firebase';
import { logError, logInfo } from '../../../../../utils/logger';
import { Toast } from '@common/Toast';

export const SharedMembersModal = ({ isOpen, onClose, survey }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sharedUsers, setSharedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState('');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isOpen && survey) {
      loadSharedUsers();
    }
  }, [isOpen, survey]);

  const loadSharedUsers = async () => {
    if (!survey?.id) return;
    
    setLoading(true);
    try {
      // Get shared maps for this survey
      const sharedMapsRef = collection(db, 'shared_maps');
      const sharedMapsQuery = query(sharedMapsRef, where('surveyId', '==', survey.id));
      const sharedMapsSnapshot = await getDocs(sharedMapsQuery);
      
      // Extract all user emails from shared maps
      const userEmails = new Set();
      sharedMapsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.type === 'selected' && Array.isArray(data.selectedUsers)) {
          data.selectedUsers.forEach(email => userEmails.add(email));
        }
      });
      
      // Add users from survey's sharedWith array if it exists
      if (Array.isArray(survey.sharedWith)) {
        survey.sharedWith.forEach(email => userEmails.add(email));
      }
      
      // Convert to array and load user details where possible
      const users = Array.from(userEmails).map(email => ({ email, name: null }));
      
      // Try to get user profiles for these emails
      const userProfiles = [];
      for (const user of users) {
        try {
          const userProfilesRef = collection(db, 'userProfiles');
          const userProfilesQuery = query(userProfilesRef, where('email', '==', user.email));
          const userProfilesSnapshot = await getDocs(userProfilesQuery);
          
          if (!userProfilesSnapshot.empty) {
            const userData = userProfilesSnapshot.docs[0].data();
            user.name = userData.name || user.email.split('@')[0];
          } else {
            user.name = user.email.split('@')[0];
          }
          
          userProfiles.push(user);
        } catch (error) {
          // If we can't get the profile, just use the email
          userProfiles.push(user);
        }
      }
      
      setSharedUsers(userProfiles);
      setLoading(false);
      logInfo('Shared users loaded successfully');
    } catch (error) {
      logError('Failed to load shared users:', error);
      setToast({
        type: 'error',
        message: 'Failed to load shared users'
      });
      setLoading(false);
    }
  };

  const handleAddEmail = () => {
    if (!emailInput.trim()) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      setToast({
        type: 'error',
        message: 'Please enter a valid email address'
      });
      return;
    }
    
    // Check if email already exists
    if (sharedUsers.some(user => user.email === emailInput)) {
      setToast({
        type: 'warning',
        message: 'This email is already in the shared list'
      });
      return;
    }
    
    // Add new email
    setSharedUsers([...sharedUsers, { email: emailInput, name: emailInput.split('@')[0] }]);
    setEmailInput('');
    
    // In a real app, you would update the shared_maps collection here
    setToast({
      type: 'success',
      message: 'User added to shared list'
    });
  };

  const handleRemoveUser = (email) => {
    setSharedUsers(sharedUsers.filter(user => user.email !== email));
    
    // In a real app, you would update the shared_maps collection here
    setToast({
      type: 'success',
      message: 'User removed from shared list'
    });
  };

  // Filter users based on search
  const filteredUsers = sharedUsers.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Shared With">
      <div className="p-6 space-y-6">
        {/* Add User Section */}
        <div className="space-y-2">
          <h3 className="text-lg font-medium text-white">Add User</h3>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="Enter email address"
                className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            </div>
            <Button
              onClick={handleAddEmail}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Search Users */}
        <div className="space-y-2">
          <h3 className="text-lg font-medium text-white">Shared Users</h3>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search users..."
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          </div>
        </div>

        {/* Users List */}
        <div className="bg-gray-700/50 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-4 text-center text-gray-400">
              Loading shared users...
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="max-h-60 overflow-y-auto">
              {filteredUsers.map((user, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 border-b border-gray-700 last:border-b-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-600 rounded-full">
                      <User className="w-5 h-5 text-gray-300" />
                    </div>
                    <div>
                      <div className="text-white font-medium">{user.name}</div>
                      <div className="text-sm text-gray-400">{user.email}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveUser(user.email)}
                    className="p-1 hover:bg-gray-600 rounded-full"
                  >
                    <X className="w-5 h-5 text-gray-400 hover:text-white" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-400">
              {sharedUsers.length === 0 ? 'No shared users yet' : 'No users match your search'}
            </div>
          )}
        </div>

        <Button
          onClick={onClose}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
        >
          Done
        </Button>
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Modal>
  );
};

export default SharedMembersModal;